import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Grid,
  Column,
  Tile,
  Button,
  Tag,
  InlineLoading,
  InlineNotification,
} from "@carbon/react";
import {
  ArrowLeft,
  Location,
  Image as ImageIcon,
  ChevronLeft,
  ChevronRight,
  Building,
  Home,
  Calendar,
  Checkmark,
  Warning,
  Information,
  User,
  Money,
  Ruler,
  Lightning,
  Store,
  Person,
  Launch,
  DirectionStraightRight,
} from "@carbon/icons-react";

import { callApi } from "../../../services/apiService";

// =====================================================================
//  Helpers used inside template conditions
// =====================================================================

// Resale property type values arrive as either IDs or labels.
// These helpers normalize and match by keyword so `condition` works
// regardless of whether the API returns "5" or "Independent House / Villa".

const APARTMENT_KEYWORDS = ["apartment", "gated community villa"];
const INDEPENDENT_KEYWORDS = ["independent house", "independent"];

const _norm = (v) => String(v || "").toLowerCase();

const isApartmentType = (value) => {
  const s = _norm(value);
  return APARTMENT_KEYWORDS.some((kw) => s.includes(kw));
};

const isIndependentHouseType = (value) => {
  const s = _norm(value);
  // Exclude gated community villas — those behave like apartments
  // (apartment name, no plot area).
  if (s.includes("gated community")) return false;
  return INDEPENDENT_KEYWORDS.some((kw) => s.includes(kw));
};

// Determine if a property is Apartment-like vs Independent-House-like
// based on `apartment_type` (or fallbacks) regardless of ID vs label.
const resolveResidentKind = (attributes, attributeValues) => {
  const raw =
    attributes?.apartment_type ??
    attributes?.resale_property_type ??
    attributes?.flat_apartment_type ??
    "";

  let label = String(raw || "");
  if (/^\d+$/.test(label)) {
    const match = attributeValues.find((a) => String(a.id) === label);
    if (match) label = match.attr_val_name || match.label || label;
  }

  const s = label.toLowerCase();

  if (s.includes("independent")) return "independent";
  if (s.includes("gated community")) return "gated";
  if (s.includes("standalone")) return "standalone";        // 👈 ADD
  if (s.includes("apartment")) return "apartment";
  return "unknown";
};

// Fields whose stored value is an attribute-value ID (should be resolved to label)
const ID_BASED_FIELD_KEYS = new Set([
  "apartment_type",
  "bhk_type",
  "facing",
  "property_age",
  "floor_type",
  "rent_furnishing",
  "rent_parking",
  "rent_monthly_maintenance",
  "rent_preferred_tenants",
  "rent_property_available_for",
  "rent_negotiable",
  "resale_property_type",
  "resale_bhk_type",
  "resale_facing",
  "resale_ownership_type",
  "resale_property_age",
  "resale_floor_type",
  "ren_resale_furnishing",
  "ren_resale_kitchen_type",
  "ren_resale_parking",
  "ren_resale_price_negotiable",
  "ren_resale_currently_under_loan",
  "flat_apartment_type",
  "flat_bhk_type",
  "flat_room_type",
  "flat_tenant_type",
  "flat_facing",
  "flat_furnishing",
  "flat_parking",
  "flat_property_age",
  "flat_floor_type",
  "flat_rent_negotiable",
  "pg_place_available_for",
  "pg_preferred_guests",
  "pg_food_option",
  "pg_hostel_rules",
  "pg_parking_type",
  "pg_available_amenities",
  "room_available_room_types",
  "room_amenities",
  "com_rent_property_type",
  "com_rent_building_type",
  "com_rent_age_of_property",
  "com_rent_furnishing",
  "com_rent_other_features",
 "com_sale_other_features",
  "com_rent_negotiable",
  "com_deposit_negotiable",
  "com_maintenance_extra",
  "com_ideal_for",
  "com_sale_property_type",
  "com_sale_building_type",
  "com_sale_price_negotiable",
  "com_business_currently_running", 
  "com_parking",
  "com_lift",
  "com_security",
  "com_power_backup",
  "com_water_storage",
  "com_property_condition",
]);

// Fields whose value is a raw number/string — NEVER resolve to label
const RAW_VALUE_FIELD_KEYS = new Set([
  "floor",
  "total_floor",
  "no_of_floors",
  "resale_floor",
  "resale_total_floor",
  "resale_no_of_floors",
  "flat_floor",
  "flat_total_floor",
  "com_rent_floor",
  "com_rent_total_floor",
  "floors_allowed_for_construction",
]);

// =====================================================================
//  TEMPLATES — one entry per (type, postedFor, isRental) variant.
// =====================================================================
const TEMPLATES = {
  // ---------------------------------------------------------------
  // Residential Rent
  // ---------------------------------------------------------------
  "residential:rent": {
    sections: [
      {
        title: "Rental Details",
        icon: "money",
        fields: [
          {
            key: "rent_expected_lease_amount",
            label: "Expected Lease Amount",
            type: "currency",
            condition: (v) =>
              String(v.rent_property_available_for || "")
                .toLowerCase()
                .includes("lease"),
          },
          {
            key: "rent_expected_rent",
            label: "Expected Rent",
            type: "currency",
            condition: (v) =>
              !String(v.rent_property_available_for || "")
                .toLowerCase()
                .includes("lease"),
          },
          {
  key: "rent_expected_deposit",
  label: "Deposit",
  type: "currency",
  condition: (v) =>
    !String(v.rent_property_available_for || "")
      .toLowerCase()
      .includes("lease"),
},
          { key: "rent_negotiable", label: "Rent Negotiable", type: "boolean" },
          { key: "rent_monthly_maintenance", label: "Monthly Maintenance", type: "text" },
{
  key: "rent_maintenance_amount",
  label: "Maintenance Amount",
  type: "currency",
  condition: (v) => {
    const maint = String(v.rent_monthly_maintenance || "").toLowerCase();

    // Only show the amount when maintenance is "Extra"
    const isExtra = maint.includes("extra");
    if (!isExtra) return false;

    return (
      v.rent_maintenance_amount !== undefined &&
      v.rent_maintenance_amount !== null &&
      v.rent_maintenance_amount !== "" &&
      !isNaN(Number(v.rent_maintenance_amount))
    );
  },
},
          { key: "rent_available_from", label: "Available From", type: "date" },
          { key: "rent_preferred_tenants", label: "Preferred Tenants", type: "array" },
          { key: "rent_property_available_for", label: "Property Available For", type: "text" },
          { key: "rent_furnishing", label: "Furnishing", type: "text" },
          { key: "rent_parking", label: "Parking", type: "text" },
          { key: "rent_description", label: "Description", type: "text" },
        ],
      },
{
  title: "Property Details",
  icon: "building",
  fields: [
    { key: "apartment_name", label: "Apartment Name", type: "text" },
    { key: "apartment_type", label: "Property Type", type: "text" },
    { key: "bhk_type", label: "BHK Type", type: "text" },
    { key: "facing", label: "Facing", type: "text" },
    { key: "floor_type", label: "Floor Type", type: "text" },
    { key: "floor", label: "Floor", type: "text" },
    { key: "total_floor", label: "Total Floor", type: "number" },
    { key: "no_of_floors", label: "No. of Floors", type: "number" },  // 👈 ADD THIS
    { key: "property_age", label: "Property Age", type: "text" },
    {
      key: "_built_up_area",
      label: "Built-up Area",
      type: "area",
      areaKey: "built_up_area",
      unitKey: "built_up_area_unit",
    },
  ],
},
      {
        title: "Amenities",
        icon: "lightning",
        fields: [
          { key: "bathrooms", label: "Bathrooms", type: "number", source: "amenities" },
          { key: "balcony", label: "Balcony", type: "number", source: "amenities" },
          { key: "gated_security", label: "Gated Security", type: "boolean", source: "amenities" },
          { key: "gym", label: "Gym", type: "boolean", source: "amenities" },
          { key: "non_veg_allowed", label: "Non-Veg Allowed", type: "boolean", source: "amenities" },
          { key: "pet_allowed", label: "Pet Allowed", type: "boolean", source: "amenities" },
          { key: "property_condition", label: "Property Condition", type: "text", source: "amenities" },
          { key: "water_supply", label: "Water Supply", type: "text", source: "amenities" },
        ],
      },
    ],
  },

  // ---------------------------------------------------------------
  // Residential Resale
  // ---------------------------------------------------------------
  "residential:resale": {
    sections: [
      {
        title: "Property Details",
        icon: "home",
        fields: [
          { key: "resale_property_type", label: "Property Type", type: "text" },
          {
            key: "resale_apartment_name",
            label: "Apartment Name",
            type: "text",
            condition: (v) => isApartmentType(v.resale_property_type),
          },
          { key: "resale_bhk_type", label: "BHK Type", type: "text" },
          { key: "resale_facing", label: "Facing", type: "text" },
          { key: "resale_ownership_type", label: "Ownership", type: "text" },
          { key: "resale_property_age", label: "Property Age", type: "text" },
          { key: "resale_floor_type", label: "Floor Type", type: "text" },
          { key: "resale_floor", label: "Floor", type: "number" },
          { key: "resale_total_floor", label: "Total Floors", type: "number" },
          { key: "resale_no_of_floors", label: "No. of Floors", type: "number" },
          {
            key: "_built_up_area_overview",
            label: "Built-up Area",
            type: "area",
            areaKey: "resale_built_up_area",
            unitKey: "built_up_area_unit",
          },
          {
            key: "resale_lease_years",
            label: "Lease Years",
            type: "number",
            condition: (v) =>
              String(v.resale_ownership_type || "")
                .toLowerCase()
                .includes("lease"),
          },
          {
            key: "_plot_area_overview",
            label: "Plot Area",
            type: "area",
            areaKey: "resale_plot_area",
            unitKey: "plot_area_unit",
            condition: (v) => isIndependentHouseType(v.resale_property_type),
          },
        ],
      },

      {
        title: "Sale Details",
        icon: "money",
        fields: [
          { key: "ren_resale_expected_price", label: "Expected Price", type: "currency" },
          { key: "ren_resale_price_negotiable", label: "Price Negotiable", type: "boolean" },
          { key: "ren_resale_currently_under_loan", label: "Under Loan", type: "boolean" },
          { key: "ren_resale_available_from", label: "Available From", type: "date" },
          { key: "ren_resale_furnishing", label: "Furnishing", type: "text" },
          { key: "ren_resale_kitchen_type", label: "Kitchen Type", type: "text" },
          { key: "ren_resale_parking", label: "Parking", type: "text" },
        ],
      },
      {
        title: "Amenities",
        icon: "lightning",
        fields: [
          { key: "resale_bathrooms", label: "Bathrooms", type: "number", source: "amenities" },
          { key: "resale_balcony", label: "Balcony", type: "number", source: "amenities" },
          { key: "resale_gated_security", label: "Gated Security", type: "boolean", source: "amenities" },
          { key: "resale_gym", label: "Gym", type: "boolean", source: "amenities" },
          { key: "resale_power_backup", label: "Power Backup", type: "text", source: "amenities" },
          { key: "resale_property_condition", label: "Property Condition", type: "text", source: "amenities" },
          { key: "resale_water_supply", label: "Water Supply", type: "text", source: "amenities" },
        ],
      },
{
  title: "Additional Info",
  icon: "information",
  fields: [
    { key: "land_ownership", label: "Land Ownership", type: "text", source: "additional" },
    { key: "lease_term", label: "Lease Term", type: "number", source: "additional" },
    { key: "encumbrance_certificate", label: "Encumbrance Certificate", type: "text", source: "additional" },
    { key: "conversion_certificate", label: "Conversion Certificate", type: "link", source: "additional" },
    { key: "rera_approved", label: "RERA Approved", type: "link", source: "additional" },
    { key: "resale_sale_deed_certificate",  label: "Sale Deed Certificate",     type: "link", source: "additional" },
    {
  key: "resale_sale_agreement",
  label: "Sale Agreement",
  type: "link",
  source: "additional",
  condition: (v) => {
    const deed = String(v.resale_sale_deed_certificate || "").toLowerCase().trim();
    // Only show Sale Agreement when Sale Deed is NOT "yes"
    return deed === "no" || deed === "don't know" || deed === "dont know";
  },
},
    { key: "resale_allotment_letter",       label: "Allotment Letter",          type: "link", source: "additional" },
    { key: "resale_occupancy_certificate",  label: "Occupancy Certificate",     type: "link", source: "additional" },
    { key: "resale_property_tax_paid",      label: "Property Tax Paid",         type: "link", source: "additional" },
    { key: "resale_other_documents",        label: "Other Documents",           type: "link", source: "additional" },
  ],
},
    ],
  },

  // ---------------------------------------------------------------
  // Flatmates
  // ---------------------------------------------------------------
  "residential:flatmates": {
    sections: [
      {
        title: "Flat Details",
        icon: "home",
        fields: [
          { key: "flat_apartment_name", label: "Apartment Name", type: "text" },
          { key: "flat_apartment_type", label: "Apartment Type", type: "text" },
          { key: "flat_bhk_type", label: "BHK Type", type: "text" },
          { key: "flat_room_type", label: "Room Type", type: "text" },
          { key: "flat_tenant_type", label: "Tenant Type", type: "text" },
          { key: "flat_facing", label: "Facing", type: "text" },
          { key: "flat_floor", label: "Floor", type: "number" },
          { key: "flat_total_floor", label: "Total Floor", type: "number" },
          { key: "flat_floor_type", label: "Floor Type", type: "text" },
          { key: "flat_furnishing", label: "Furnishing", type: "text" },
          { key: "flat_parking", label: "Parking", type: "text" },
          { key: "flat_property_age", label: "Property Age", type: "text" },
          {
            key: "_built_up_area",
            label: "Built-up Area",
            type: "area",
            areaKey: "flat_built_up_area",
            unitKey: "built_up_area_unit",
          },
{ key: "flat_description", label: "Description", type: "text" },


        ],
      },
      {
        title: "Rental Details",
        icon: "money",
        fields: [
          { key: "flat_expected_rent", label: "Expected Rent", type: "currency" },
          { key: "flat_expected_deposit", label: "Deposit", type: "currency" },
          { key: "flat_rent_negotiable", label: "Rent Negotiable", type: "boolean" },
          { key: "rent_monthly_maintenance", label: "Maintenance", type: "text" },
          {
  key: "flat_maintenance_amount",
  label: "Maintenance Amount",
  type: "currency",
  condition: (v) => {
    const maint = String(v.rent_monthly_maintenance || "").toLowerCase();
    if (!maint.includes("extra")) return false;
    return (
      v.flat_maintenance_amount !== undefined &&
      v.flat_maintenance_amount !== null &&
      v.flat_maintenance_amount !== "" &&
      !isNaN(Number(v.flat_maintenance_amount))
    );
  },
},
          { key: "flat_available_from", label: "Available From", type: "date" },
        ],
      },
      {
        title: "Amenities",
        icon: "lightning",
        fields: [
          { key: "flat_ac_room", label: "AC Room", type: "boolean", source: "amenities" },
          { key: "flat_attached_bathroom", label: "Attached Bathroom", type: "boolean", source: "amenities" },
          { key: "flat_balcony", label: "Balcony", type: "boolean", source: "amenities" },
          { key: "flat_gated_security", label: "Gated Security", type: "boolean", source: "amenities" },
          { key: "flat_gym", label: "Gym", type: "boolean", source: "amenities" },
          { key: "flat_water_supply", label: "Water Supply", type: "text", source: "amenities" },
          { key: "flat_drinking_allowed", label: "Drinking Allowed", type: "boolean", source: "amenities" },
          { key: "flat_non_veg_allowed", label: "Non-Veg Allowed", type: "boolean", source: "amenities" },
          { key: "flat_smoking_allowed", label: "Smoking Allowed", type: "boolean", source: "amenities" },
          { key: "flat_amenities", label: "Available Amenities", type: "array", source: "amenities" },
          { key: "flat_direction_tip", label: "Directions", type: "text" },
        ],
      },
    ],
  },

  // ---------------------------------------------------------------
  // PG / Hostel
  // ---------------------------------------------------------------
  "pg:hostel": {
    sections: [
      {
        title: "PG Details",
        icon: "home",
        fields: [
          { key: "pg_place_available_for", label: "Available For", type: "text" },
          { key: "pg_preferred_guests", label: "Preferred Guests", type: "text" },
          { key: "pg_food_included", label: "Food Included", type: "boolean" },
          { key: "pg_food_option", label: "Food Options", type: "array" },
          { key: "pg_available_from", label: "Available From", type: "date" },
          { key: "pg_hostel_rules", label: "Hostel Rules", type: "array" },
          { key: "pg_gate_closing_time", label: "Gate Closing Time", type: "text" },
        ],
      },
      {
        title: "Room Details",
        icon: "building",
        fields: [
          { key: "room_available_room_types", label: "Room Types", type: "array" },
          { key: "room_expected_rent", label: "Expected Rent", type: "currency" },
          { key: "room_expected_deposit", label: "Expected Deposit", type: "currency" },
          { key: "room_expected_rent_per_person", label: "Rent per Person", type: "currency" },
          { key: "room_expected_deposit_per_person", label: "Deposit per Person", type: "currency" },
          { key: "room_expected_rent_three", label: "Rent (3 Sharing)", type: "currency" },
          { key: "room_expected_deposit_three", label: "Deposit (3 Sharing)", type: "currency" },
          { key: "room_expected_rent_four", label: "Rent (4 Sharing)", type: "currency" },
          { key: "room_expected_deposit_four", label: "Deposit (4 Sharing)", type: "currency" },
          { key: "room_amenities", label: "Room Amenities", type: "array" },
        ],
      },
      {
        title: "Amenities",
        icon: "lightning",
        fields: [
          { key: "pg_parking_type", label: "Parking", type: "text", source: "amenities" },
          { key: "pg_laundry", label: "Laundry", type: "boolean", source: "amenities" },
          { key: "pg_room_cleaning", label: "Room Cleaning", type: "boolean", source: "amenities" },
          { key: "pg_warden_facility", label: "Warden Facility", type: "boolean", source: "amenities" },
          { key: "pg_available_amenities", label: "Available Amenities", type: "array", source: "amenities" },
        ],
      },
    ],
  },

  // ---------------------------------------------------------------
  // Commercial Rent
  // ---------------------------------------------------------------
  "commercial:rent": {
    sections: [
      {
        title: "Property Details",
        icon: "store",
        fields: [
          { key: "com_rent_property_type", label: "Property Type", type: "text" },
          { key: "com_rent_building_type", label: "Building Type", type: "text" },
          { key: "com_rent_floor", label: "Floor", type: "text" },
          { key: "com_rent_total_floor", label: "Total Floors", type: "number" },
          {
            key: "_built_up_area",
            label: "Built-up Area",
            type: "area",
            areaKey: "com_sale_super_built_up_area",
            unitKey: "built_up_area_unit",
          },
          { key: "com_rent_age_of_property", label: "Property Age", type: "text" },
          { key: "com_rent_furnishing", label: "Furnishing", type: "text" },
          
          { key: "com_rent_other_features", label: "Other Features", type: "array" },
        ],
      },
      {
        title: "Rental Details",
        icon: "money",
        fields: [
          { key: "com_expected_rent", label: "Monthly Rent", type: "currency" },
          { key: "com_deposit", label: "Deposit", type: "currency" },
          { key: "com_rent_negotiable", label: "Rent Negotiable", type: "boolean" },
          { key: "com_deposit_negotiable", label: "Deposit Negotiable", type: "boolean" },
          { key: "com_lease_duration", label: "Lease Duration", type: "number" },
          { key: "com_lockin_period", label: "Lock-in Period", type: "number" },
          { key: "com_maintenance_extra", label: "Maintenance Type", type: "boolean" },
          {
  key: "com_monthly_maintenance",
  label: "Maintenance Amount",
  type: "currency",
  condition: (v) => {
    const maint = String(v.com_maintenance_extra || "").toLowerCase();
    if (!maint.includes("extra")) return false;
    return (
      v.com_monthly_maintenance !== undefined &&
      v.com_monthly_maintenance !== null &&
      v.com_monthly_maintenance !== "" &&
      !isNaN(Number(v.com_monthly_maintenance))
    );
  },
},
          { key: "com_available_from", label: "Available From", type: "date" },
          { key: "com_parking_slots", label: "Parking Slots", type: "number" },
          { key: "com_ideal_for", label: "Ideal For", type: "array" },
        ],
      },
      {
        title: "Amenities",
        icon: "lightning",
        fields: [
          { key: "com_power_backup", label: "Power Backup", type: "text", source: "amenities" },
          { key: "com_lift", label: "Lift", type: "text", source: "amenities" },
          { key: "com_parking", label: "Parking", type: "text", source: "amenities" },
          { key: "com_washroom", label: "Washroom", type: "text", source: "amenities" },
          { key: "com_water_storage", label: "Water Storage", type: "text", source: "amenities" },
          { key: "com_security", label: "Security", type: "text", source: "amenities" },
          { key: "wifi", label: "WiFi", type: "text", source: "amenities" },

          { key: "com_property_condition", label: "Property Condition", type: "text", source: "amenities" },
          { key: "com_business_currently_running", label: "Current Business Running", type: "text", source: "amenities" },
          
        ],
      },
      {
        title: "Additional Information",
        icon: "information",
        fields: [
          { key: "land_ownership", label: "Land Ownership", type: "text", source: "additional" },
          { key: "lease_term", label: "Lease Term", type: "number", source: "additional" },
          { key: "encumbrance_certificate", label: "Encumbrance Certificate", type: "text", source: "additional" },
          { key: "conversion_certificate", label: "Conversion Certificate", type: "text", source: "additional" },
          { key: "rera_approved", label: "RERA Approved", type: "text", source: "additional" },
          { key: "resale_sale_deed_certificate", label: "Sale Deed Certificate", type: "link", source: "additional" },
        ],
      },
    ],
  },

  // ---------------------------------------------------------------
  // Commercial Sale
  // ---------------------------------------------------------------
  "commercial:resale": {
    sections: [
{
  title: "Property Details",
  icon: "store",
  fields: [
    { key: "com_sale_property_type", label: "Property Type", type: "text" },
    { key: "com_sale_building_type", label: "Building Type", type: "text" },
    { key: "floor", label: "Floor", type: "number" },
    { key: "total_floor", label: "Total Floors", type: "number" },
    {
      key: "_built_up_area",
      label: "Built-up Area",
      type: "area",
      areaKey: "com_sale_super_built_up_area",
      unitKey: "built_up_area_unit",
    },
    { key: "property_age", label: "Property Age", type: "text" },
    { key: "resale_ownership_type", label: "Ownership", type: "text" },
    {
      key: "com_rent_other_features",
      label: "Other Features",
      type: "array",
    },
    { key: "com_rent_furnishing", label: "Furnishing", type: "text" },
  ],
},
      {
        title: "Sale Details",
        icon: "money",
        fields: [
          { key: "com_sale_expected_price", label: "Expected Price", type: "currency" },
          { key: "com_sale_price_negotiable", label: "Price Negotiable", type: "boolean" },
          { key: "com_sale_available_from", label: "Available From", type: "date" },
        ],
      },
      {
        title: "Amenities & Features",
        icon: "lightning",
        fields: [
          // Change "text" to "array" for multi-select amenities
          { key: "com_power_backup", label: "Power Backup", type: "array", source: "amenities" },
          { key: "com_lift", label: "Lift", type: "array", source: "amenities" },
          { key: "com_parking", label: "Parking", type: "array", source: "amenities" },
          { key: "com_parking_slots", label: "Parking Slots", type: "number", source: "amenities" },
          { key: "com_sale_washrooms", label: "Washrooms", type: "text", source: "amenities" },
          { key: "com_water_storage", label: "Water Storage", type: "array", source: "amenities" },
          { key: "com_security", label: "Security", type: "array", source: "amenities" },
          { key: "wifi", label: "WiFi", type: "text", source: "amenities" },
          { key: "com_property_condition", label: "Property Condition", type: "text", source: "amenities" },
          { key: "com_sale_current_business_running", label: "Current Business Running", type: "text", source: "amenities" },
        ],
      },
      {
        title: "Additional Information",
        icon: "information",
        fields: [
          { key: "com_property_description", label: "Property Description", type: "text", source: "additional" },
          { key: "com_previous_occupancy", label: "Previous Occupancy", type: "text", source: "additional" },
          { key: "com_property_cleaning", label: "Property Cleaning", type: "text", source: "additional" },
          { key: "com_property_painting", label: "Property Painting", type: "text", source: "additional" },
        ],
      },
    ],
  },

  // ---------------------------------------------------------------
  // Land / Plot
  // ---------------------------------------------------------------
  "land:resale": {
    sections: [
      {
        title: "Property Details",
        icon: "home",
        fields: [
          { key: "project_name", label: "Project Name", type: "text" },
          { key: "facing", label: "Facing", type: "text" },
          { key: "boundary_wall", label: "Boundary Wall", type: "boolean" },
          { key: "gated_project", label: "Gated Project", type: "boolean" },
          { key: "floors_allowed_for_construction", label: "Floors Allowed", type: "number" },
                    {
            key: "_land_area",
            label: "Land Area",
            type: "area",
            areaKey: "land_plot_area",
            unitKey: "plot_area_unit",
          },
          { key: "_length", label: "Length", type: "areaValue", areaKey: "length", suffix: " ft" },
          { key: "_width", label: "Width", type: "areaValue", areaKey: "width", suffix: " ft" },
        ],
      },
      {
        title: "Sale Details",
        icon: "money",
        fields: [
          { key: "land_resale_expected_price", label: "Expected Price", type: "currency", suffix: " / cent" },
          { key: "land_resale_price_negotiable", label: "Price Negotiable", type: "boolean" },
          { key: "land_resale_currently_under_loan", label: "Under Loan", type: "boolean" },
          { key: "land_resale_available_from", label: "Available From", type: "date" },
          { key: "land_resale_description", label: "Description", type: "text" },
        ],
      },
      {
        title: "Amenities",
        icon: "lightning",
        fields: [
          { key: "water_supply", label: "Water Supply", type: "text", source: "amenities" },
          { key: "electricity_connection", label: "Electricity Connection", type: "text", source: "amenities" },
          { key: "sewage_connection", label: "Sewage Connection", type: "text", source: "amenities" },
          { key: "gated_security", label: "Gated Security", type: "boolean", source: "amenities" },
          { key: "width_facing_road", label: "Width of Facing Road", type: "number", source: "amenities" },
        ],
      },
      {
        title: "Additional Information",
        icon: "information",
        fields: [
          { key: "land_ownership", label: "Land Ownership", type: "text", source: "additional" },
          { key: "lease_term", label: "Lease Term", type: "number", source: "additional" },
          { key: "encumbrance_certificate", label: "Encumbrance Certificate", type: "text", source: "additional" },
          { key: "conversion_certificate", label: "Conversion Certificate", type: "text", source: "additional" },
          { key: "rera_approved", label: "RERA Approved", type: "text", source: "additional" },
        ],
      },
    ],
  },
};

// Pick the template key from property metadata
const resolveTemplateKey = (property, details) => {
  const type = String(details?.type || "").toLowerCase();
  const postedFor = String(details?.postedFor || "").toLowerCase();
  const isRental = details?.isRental === true;

  if (postedFor === "flatmates") return "residential:flatmates";
  if (postedFor === "pg/hostel") return "pg:hostel";
  if (type === "land/plot") return "land:resale";
  if (type === "commercial") return isRental ? "commercial:rent" : "commercial:resale";
  if (type === "residential") return isRental ? "residential:rent" : "residential:resale";
  return null;
};

// =====================================================================
//  MAIN COMPONENT
// =====================================================================
const PropertyView = () => {
  const navigate = useNavigate();
  const { propertyUuid } = useParams();

  const [property, setProperty] = useState(null);
  const [media, setMedia] = useState([]);
  const [videos, setVideos] = useState([]);
  const [propertyDocuments, setPropertyDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mediaLoading, setMediaLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [attributeValues, setAttributeValues] = useState([]);

  useEffect(() => {
    if (propertyUuid) {
      fetchPropertyDetails();
      fetchPropertyMedia();
      fetchPropertyDocuments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyUuid]);

  const fetchPropertyDocuments = async () => {
    try {
      const response = await callApi(
        "GET",
        `/property-documents/?property_uuid=${encodeURIComponent(propertyUuid)}`
      );
      const data = Array.isArray(response?.data) ? response.data : [];
      setPropertyDocuments(data);
    } catch (err) {
      console.error("❌ Failed to fetch property documents:", err);
    }
  };

  useEffect(() => {
    const fetchAttributeValues = async () => {
      try {
        const res = await callApi("GET", "/attribute-values/");
        if (res?.data) {
          setAttributeValues(
            res.data.map((attr) => ({
              id: attr.id,
              label: attr.attr_val_name,
              attr_val_name: attr.attr_val_name,
              attrName: attr.attr_name,
            }))
          );
        }
      } catch (err) {
        console.error("attribute-values fetch failed:", err);
      }
    };
    fetchAttributeValues();
  }, []);

  const fetchPropertyDetails = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await callApi(
        "GET",
        `/properties/?property_uuid=${encodeURIComponent(propertyUuid)}`
      );
      const data = Array.isArray(response?.data) ? response.data[0] : response?.data;
      if (!data) {
        setProperty(null);
        setError("Property details not found.");
        return;
      }
      setProperty(data);
      setCurrentImageIndex(0);
    } catch (err) {
      console.error("❌ Failed to fetch property:", err);
      setError(err?.response?.data?.message || "Failed to load property details.");
    } finally {
      setLoading(false);
    }
  };

  const fetchPropertyMedia = async () => {
    try {
      setMediaLoading(true);
      const response = await callApi(
        "GET",
        `/property-media/?property_uuid=${encodeURIComponent(propertyUuid)}`
      );
      const data = Array.isArray(response?.data) ? response.data : [];

      const PHOTO_ID = 882;
      const VIDEO_ID = 883;

      const isVideoItem = (item) => {
        const attrId = Number(
          item?.ATTRIBUTE_VALUE_ID ??
          item?.attribute_value_id ??
          item?.attributeValueId ??
          0
        );
        if (attrId === VIDEO_ID) return true;
        if (attrId === PHOTO_ID) return false;
        const url = getMediaUrl(item).toLowerCase();
        return /\.(mp4|mov|avi|webm|mkv)(\?.*)?$/.test(url);
      };

      const tagged = data.map((item) => ({
        ...item,
        __mediaType: isVideoItem(item) ? "video" : "image",
      }));

      const photos = tagged.filter((i) => i.__mediaType === "image");
      const vids = tagged.filter((i) => i.__mediaType === "video");

      // ORDER: images first, then videos
      const ordered = [...photos, ...vids];

      setMedia(ordered);
      setVideos(vids);
      setCurrentImageIndex(0);
    } catch (err) {
      console.error("❌ Failed to fetch property media:", err);
    } finally {
      setMediaLoading(false);
    }
  };

  const propertyDetails = useMemo(() => {
    if (!property) return null;

    // Map document type names (from /property-documents/) to the
    // attribute keys the Documents template expects.
    const DOCUMENT_TYPE_TO_KEY = {
      "Sale Deed Certificate": "resale_sale_deed_certificate",
      "Sale Agreement": "resale_sale_agreement",
      "Property Tax Receipt": "resale_property_tax_paid",
      "Occupancy Certificate": "resale_occupancy_certificate",
      "Allotment Letter": "resale_allotment_letter",
      "Other Documents": "resale_other_documents",
    };

    const documentsFromApi = {};
    (Array.isArray(propertyDocuments) ? propertyDocuments : []).forEach((doc) => {
      const typeName =
        doc?.ATTRIBUTE_VALUE_NAME ||
        doc?.attribute_value_name ||
        doc?.ATTRIBUTE_NAME ||
        doc?.attribute_name;
      const url = doc?.DOCUMENT_URL || doc?.document_url || doc?.FILE_URL;
      if (!typeName || !url) return;
      const key = DOCUMENT_TYPE_TO_KEY[typeName];
      if (key) documentsFromApi[key] = url;
    });

    return {
      id: property.id,
      propertySeqId: property.property_seq_id || "-",
      title: property.title || "Property Details",
      description: property.description || "",
      type: property.type || "N/P",
      postedFor: property.posted_for || "N/P",
      startingPrice: property.starting_price,
      endingPrice: property.ending_price,
      rentAmount: property.rent_amount,
      verificationStatus: property.verification_status || "-",
      status: property.status || "-",
      createdUser: property.created_user || "-",
      createdTimestamp: property.created_timestamp || "-",
      isRental: property.is_rental || false,
      isResale: property.is_resale || false,
      location: property.location || {},
      contact: property.contact || {},
      attributes: property.attributes || {},
      areaInformation: property.area_information || {},
      amenities: property.amenities || {},
      additionalInformation: {
        ...(property.additional_information || {}),
        ...documentsFromApi,
      },
    };
  }, [property, propertyDocuments]);
  // ---------- Helpers ----------
  const formatVerificationStatus = useCallback((status) => {
    if (!status || status === "-") return "-";
    const map = {
      verified: "Verified",
      pending: "Pending",
      under_verification: "Under Verification",
      "under verification": "Under Verification",
      rejected: "Rejected",
      approved: "Approved",
    };
    return map[status.toLowerCase().trim()] || status;
  }, []);

  const resolveAttrLabelById = useCallback(
    (id) => {
      if (id === null || id === undefined || id === "") return null;
      const match = attributeValues.find((a) => String(a.id) === String(id));
      return match?.attr_val_name || match?.label || null;
    },
    [attributeValues]
  );

  const resolveAttrLabels = useCallback(
    (raw) => {
      if (raw === null || raw === undefined || raw === "") return null;
      if (Array.isArray(raw) && raw.length === 0) return null;
      const ids = Array.isArray(raw)
        ? raw
        : String(raw)
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);

      const labels = ids
        .map((id) => {
          const match = attributeValues.find(
            (a) =>
              String(a.id) === String(id) ||
              String(a.attr_val_name).toLowerCase() === String(id).toLowerCase()
          );
          return match?.attr_val_name || match?.label || id;
        })
        .filter(Boolean);

      return labels.length ? labels.join(", ") : null;
    },
    [attributeValues]
  );

  const template = useMemo(() => {
    if (!propertyDetails) return null;
    const key = resolveTemplateKey(property, propertyDetails);
    return key ? TEMPLATES[key] : null;
  }, [property, propertyDetails]);

  const readRaw = useCallback(
    (field) => {
      const source = field.source || "attributes";
      let raw;

if (field.type === "area") {
  const areaInfo = propertyDetails?.areaInformation || {};
  const areaVal = areaInfo[field.areaKey];
  let unitVal = areaInfo[field.unitKey];

  if (
    areaVal === null ||
    areaVal === undefined ||
    areaVal === "" ||
    Number(areaVal) === 0
  )
    return null;

  // 🔥 Resolve numeric unit IDs → labels (e.g. 457 → "Sq.ft")
  if (
    unitVal !== null &&
    unitVal !== undefined &&
    unitVal !== "" &&
    /^\d+$/.test(String(unitVal))
  ) {
    const resolvedUnit = resolveAttrLabelById(unitVal);
    if (resolvedUnit) unitVal = resolvedUnit;
  }

  return { area: areaVal, unit: unitVal || "" };
}

      if (field.type === "areaValue") {
        const v = propertyDetails?.areaInformation?.[field.areaKey];
        if (v === null || v === undefined || v === "" || Number(v) === 0)
          return null;
        return v;
      }

      if (source === "amenities") raw = propertyDetails?.amenities?.[field.key];
      else if (source === "additional")
        raw = propertyDetails?.additionalInformation?.[field.key];
      else raw = propertyDetails?.attributes?.[field.key];

      return raw;
    },
    [propertyDetails, resolveAttrLabelById]
  );

  const formatValue = useCallback(
    (field, raw) => {
      if (raw === null || raw === undefined || raw === "") return null;

      switch (field.type) {
        case "currency": {
          const n = Number(raw);
          if (isNaN(n) || n === 0) return null;
          return `₹${n.toLocaleString("en-IN")}${field.suffix || ""}`;
        }
case "area": {
  if (!raw?.area) return null;
  // If the unit is still a raw number (couldn't resolve), don't show it
  const unit = /^\d+$/.test(String(raw.unit || "")) ? "" : (raw.unit || "");
  return `${raw.area} ${unit}`.trim();
}
        case "areaValue": {
          return `${raw}${field.suffix || ""}`;
        }
        case "number": {
          const n = Number(raw);
          if (isNaN(n)) return String(raw);
          return String(n);
        }
        case "boolean": {
          if (
            raw === true ||
            raw === 1 ||
            raw === "1" ||
            String(raw).toLowerCase() === "yes" ||
            String(raw).toLowerCase() === "true"
          )
            return "Yes";
          if (
            raw === false ||
            raw === 0 ||
            raw === "0" ||
            String(raw).toLowerCase() === "no" ||
            String(raw).toLowerCase() === "false"
          )
            return "No";
          const lbl = resolveAttrLabelById(raw);
          if (lbl) {
            if (lbl.toLowerCase() === "yes") return "Yes";
            if (lbl.toLowerCase() === "no") return "No";
            return lbl;
          }
          return String(raw);
        }
        case "array":
          return resolveAttrLabels(raw);
case "link": {
  if (Array.isArray(raw)) {
    return raw.length > 0 ? "Yes" : null;
  }
  if (typeof raw === "object" && raw !== null) {
    return Object.keys(raw).length > 0 ? "Yes" : null;
  }
  const s = String(raw).trim();
  if (!s) return null;

  // If it's a URL or file path → a document exists → "Yes"
  if (
    /^https?:\/\//i.test(s) ||
    /\.(pdf|jpg|jpeg|png|doc|docx)$/i.test(s) ||
    s.startsWith("/") ||
    s.startsWith("data:")
  ) {
    return "Yes";
  }

  // If it's a numeric ID → try to resolve to its label
  if (/^\d+$/.test(s)) {
    const resolved = resolveAttrLabelById(s);
    if (resolved) {
      const lowerR = resolved.toLowerCase();
      if (lowerR === "yes" || lowerR === "true") return "Yes";
      if (lowerR === "no" || lowerR === "false") return "No";
      return resolved; // e.g. "Patta Documents"
    }
    // Unresolvable numeric ID → hide it (was showing "472")
    return null;
  }

  // Plain string label — check Yes/No, else return as-is ("Don't Know")
  const lower = s.toLowerCase();
  if (lower === "yes" || lower === "true" || s === "1") return "Yes";
  if (lower === "no" || lower === "false" || s === "0") return "No";
  return s;
}
        case "date":
          return formatDateOnly(raw);
        case "text":
        default: {
          const s = String(raw);
          const looksLikeId = /^\d+$/.test(s);
          const fieldKey = field.key;

          if (
            looksLikeId &&
            ID_BASED_FIELD_KEYS.has(fieldKey) &&
            !RAW_VALUE_FIELD_KEYS.has(fieldKey)
          ) {
            const lbl = resolveAttrLabelById(raw);
            if (lbl) return lbl;
          }
          return s;
        }
      }
    },
    [resolveAttrLabelById, resolveAttrLabels]
  );

  const renderedSections = useMemo(() => {
    if (!template || !propertyDetails) return [];

    const savedValues = {
      ...propertyDetails.attributes,
      ...propertyDetails.amenities,
      ...propertyDetails.additionalInformation,
    };

    // Build a copy where numeric IDs are replaced by their labels, so
    // `condition` functions that compare against strings ("Independent
    // House", "Apartment") work correctly regardless of storage format.
    const resolvedValues = { ...savedValues };
    Object.keys(savedValues).forEach((k) => {
      const v = savedValues[k];
      if (
        (typeof v === "number" || /^\d+$/.test(String(v || ""))) &&
        ID_BASED_FIELD_KEYS.has(k) &&
        !RAW_VALUE_FIELD_KEYS.has(k)
      ) {
        const lbl = resolveAttrLabelById(v);
        if (lbl) resolvedValues[k] = lbl;
      }
    });

    // 🔥 Determine the resident kind (apartment vs independent vs gated)
    // so we can HIDE stale fields the backend never deleted.
    const residentKind = resolveResidentKind(
      propertyDetails.attributes,
      attributeValues
    );

    // Fields that must never appear for each resident kind
    // Fields that must never appear for each resident kind
    // Note: resale templates use resale_* prefixed keys, so we list both
    const STALE_FIELDS_FOR_KIND = {
      independent: [
        "apartment_name", "resale_apartment_name",
        "floor", "resale_floor",
        "total_floor", "resale_total_floor",
      ],
      gated: [
        "floor", "resale_floor",
        "total_floor", "resale_total_floor",
      ],
      apartment: [
        "no_of_floors", "resale_no_of_floors",
      ],
      standalone: [
        "apartment_name", "resale_apartment_name",
        "no_of_floors", "resale_no_of_floors",
      ],
      unknown: [],
    };

    const hiddenKeys = new Set(STALE_FIELDS_FOR_KIND[residentKind] || []);

    return template.sections
      .map((section) => {
        const items = section.fields
          .filter((f) => !hiddenKeys.has(f.key))                    // 🔥 hide stale
          .filter((f) => !f.condition || f.condition(resolvedValues))
          .filter(
            (f) => !(f.key === "description" || /_description$/i.test(f.key))
          )
          .map((field) => {
            const raw = readRaw(field);
            const display = formatValue(field, raw);
            return { field, raw, display };
          })
          .filter(({ display }) => display !== null && display !== "");

        return { ...section, items };
      })
      .filter((s) => s.items.length > 0);
  }, [
    template,
    propertyDetails,
    readRaw,
    formatValue,
    resolveAttrLabelById,
    attributeValues,
  ]);

  // =====================================================================
//  Collect description from ANY property type into a single value
//  so it can be rendered as its own card.
// =====================================================================
const propertyDescription = useMemo(() => {
  if (!propertyDetails) return null;

  const isDescKey = (k) =>
    k === "description" || /_description$/i.test(k);

  // Auto-generated placeholder the backend injects when the user
  // leaves description empty, e.g. "Land/Plot for resale",
  // "Residential for rent", "Commercial for sale", etc.
  const isAutoGenerated = (val) => {
    const s = String(val || "").trim().toLowerCase();
    if (!s) return true;
    return /^(land\s*\/\s*plot|residential|commercial|pg\s*\/\s*hostel|flatmates)\s+for\s+(resale|sale|rent|lease)$/.test(
      s
    );
  };

  // 1) Scan all attribute pools in priority order
  const pools = [
    propertyDetails.attributes,             // flat_description, rent_description, resale_description, etc.
    propertyDetails.additionalInformation,  // com_property_description
    propertyDetails.amenities,
  ];

  for (const pool of pools) {
    if (!pool) continue;
    for (const key of Object.keys(pool)) {
      if (isDescKey(key) && pool[key]) {
        const val = String(pool[key]).trim();
        if (val && !isAutoGenerated(val)) return val;
      }
    }
  }

  // 2) Fall back to top-level properties.description, unless it's
  //    the auto-generated placeholder.
  if (propertyDetails.description) {
    const val = String(propertyDetails.description).trim();
    if (val && !isAutoGenerated(val)) return val;
  }

  return null;
}, [propertyDetails]);

  const formattedPrice = useMemo(() => {
    const attrs = propertyDetails?.attributes || {};

    if (propertyDetails?.postedFor?.toLowerCase() === "flatmates") {
      const rent = attrs.flat_expected_rent;
      if (rent) return `₹${Number(rent).toLocaleString("en-IN")}`;
    }
    if (propertyDetails?.postedFor?.toLowerCase() === "pg/hostel") {
      const rent =
        attrs.room_expected_rent_per_person ||
        attrs.room_expected_rent ||
        attrs.room_expected_rent_three ||
        attrs.room_expected_rent_four;
      if (rent) return `₹${Number(rent).toLocaleString("en-IN")}`;
    }
    if (propertyDetails?.isRental) {
      const rent =
        propertyDetails?.rentAmount ||
        attrs.rent_expected_rent ||
        attrs.rent_expected_lease_amount ||
        attrs.com_expected_rent;
      if (rent) return `₹${Number(rent).toLocaleString("en-IN")}`;
    }
    const price =
      propertyDetails?.startingPrice ||
      attrs.ren_resale_expected_price ||
      attrs.com_sale_expected_price ||
      attrs.land_resale_expected_price;
    if (price) return `₹${Number(price).toLocaleString("en-IN")}`;

    return formatPrice(
      propertyDetails?.startingPrice,
      propertyDetails?.endingPrice
    );
  }, [propertyDetails]);

  const locationText = useMemo(
    () => formatLocation(propertyDetails?.location),
    [propertyDetails]
  );

  const isVerified = formatVerificationStatus(propertyDetails?.verificationStatus) === "Verified";
  const isPending = formatVerificationStatus(propertyDetails?.verificationStatus) === "Pending";
  const isUnderVerification =
    formatVerificationStatus(propertyDetails?.verificationStatus) === "Under Verification";

  const isRental = propertyDetails?.isRental || false;
  const isCommercial = propertyDetails?.type?.toLowerCase() === "commercial";
  const isLand = propertyDetails?.type?.toLowerCase() === "land/plot";
  const isPG = propertyDetails?.postedFor?.toLowerCase() === "pg/hostel";
  const isFlatmates = propertyDetails?.postedFor?.toLowerCase() === "flatmates";

  const rentAvailableForRaw = String(
    propertyDetails?.attributes?.rent_property_available_for || ""
  ).toLowerCase();
  const resolvedAvailableFor = resolveAttrLabels(
    propertyDetails?.attributes?.rent_property_available_for
  );
  const rentAvailableForLower =
    resolvedAvailableFor?.toLowerCase() || rentAvailableForRaw;

  const isLease =
    isRental && !isCommercial && rentAvailableForLower.includes("lease");
  const isOnlyRent =
    isRental &&
    !isCommercial &&
    rentAvailableForLower.includes("rent") &&
    !isLease;

  const nextImage = () => setCurrentImageIndex((prev) => (prev + 1) % media.length);
  const prevImage = () =>
    setCurrentImageIndex((prev) => (prev - 1 + media.length) % media.length);
  const goToImage = (i) => setCurrentImageIndex(i);

  if (loading) {
    return (
      <div className="property-view-loading" style={{ padding: "4rem", textAlign: "center" }}>
        <InlineLoading description="Loading property details..." />
      </div>
    );
  }
  if (error) {
    return (
      <div className="property-view-page" style={{ padding: "2rem" }}>
        <Button kind="ghost" renderIcon={ArrowLeft} onClick={() => navigate(-1)}>
          Back to Properties
        </Button>
        <InlineNotification kind="error" title="Unable to load property" subtitle={error} />
      </div>
    );
  }
  if (!property || !propertyDetails) {
    return (
      <div className="property-view-page" style={{ padding: "2rem" }}>
        <Button kind="ghost" renderIcon={ArrowLeft} onClick={() => navigate(-1)}>
          Back to Properties
        </Button>
        <p className="no-property">Property details not found.</p>
      </div>
    );
  }

  return (
    <div className="property-view-page">
      <div className="property-view-back">
        <Button kind="ghost" renderIcon={ArrowLeft} onClick={() => navigate(-1)}>
          Back to Properties
        </Button>
      </div>

      <div className="property-view-header">
        <div className="header-left">
          <div className="property-badge">
            <Tag type="teal" size="sm">
              {propertyDetails.postedFor}
            </Tag>
            <Tag type="green" size="sm">
              {propertyDetails.type}
            </Tag>
            {isVerified && (
              <Tag type="blue" size="sm" renderIcon={Checkmark}>
                Verified
              </Tag>
            )}
            {isPending && (
              <Tag type="orange" size="sm" renderIcon={Warning}>
                Pending
              </Tag>
            )}
            {isUnderVerification && (
              <Tag type="orange" size="sm" renderIcon={Warning}>
                Under Verification
              </Tag>
            )}
            {isFlatmates && (
              <Tag type="purple" size="sm">
                Flatmates
              </Tag>
            )}
          </div>
          <h1>{propertyDetails.title}</h1>
          <div className="property-location-badge">
            <Location size={16} />
            <span>{locationText.split(",")[0] || "Location"}</span>
          </div>
        </div>
      </div>

      <Grid>
        <Column lg={10} md={8} sm={4}>
          {/* IMAGE / VIDEO CAROUSEL */}
          <div className="property-image-carousel">
            {mediaLoading ? (
              <div className="image-loading">
                <InlineLoading />
              </div>
            ) : media.length > 0 ? (
              <>
                <div className="main-image-container">
                  {media[currentImageIndex]?.__mediaType === "video" ? (
                    <video
                      key={currentImageIndex}
                      controls
                      autoPlay
                      muted
                      className="main-image"
                      src={getMediaUrl(media[currentImageIndex])}
                    >
                      Your browser does not support video playback.
                    </video>
                  ) : (
                    <img
                      src={getMediaUrl(media[currentImageIndex])}
                      alt={`${propertyDetails.title} - ${currentImageIndex + 1}`}
                      className="main-image"
                    />
                  )}
                  {media.length > 1 && (
                    <>
                      <button className="carousel-btn prev" onClick={prevImage}>
                        <ChevronLeft size={32} />
                      </button>
                      <button className="carousel-btn next" onClick={nextImage}>
                        <ChevronRight size={32} />
                      </button>
                    </>
                  )}
                  <div className="image-counter">
                    {currentImageIndex + 1} / {media.length}
                  </div>
                </div>
                {media.length > 1 && (
                  <div className="thumbnail-container">
                    {media.map((item, index) => (
                      <div
                        key={index}
                        className={`thumbnail ${index === currentImageIndex ? "active" : ""}`}
                        onClick={() => goToImage(index)}
                        style={{ position: "relative" }}
                      >
                        {item.__mediaType === "video" ? (
                          <>
                            <video
                              src={getMediaUrl(item)}
                              muted
                              preload="metadata"
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                display: "block",
                              }}
                            />
                            <div
                              style={{
                                position: "absolute",
                                inset: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: "rgba(0,0,0,0.35)",
                                color: "#fff",
                                fontSize: "1.5rem",
                                pointerEvents: "none",
                                borderRadius: "4px",
                              }}
                            >
                              ▶
                            </div>
                          </>
                        ) : (
                          <img src={getMediaUrl(item)} alt={`Thumbnail ${index + 1}`} />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="no-image">
                <ImageIcon size={48} />
                <span>No property image available</span>
              </div>
            )}
          </div>

          <KeyFeatureGrid
            renderedSections={renderedSections}
            formattedPrice={formattedPrice}
            isFlatmates={isFlatmates}
            isPG={isPG}
            isRental={isRental}
            isLand={isLand}
            isCommercial={isCommercial}
            isLease={isLease}
            isOnlyRent={isOnlyRent}
          />

          <div className="sections-container">
            {renderedSections.map((section) => (
              <PropertySection
                key={section.title}
                title={section.title}
                icon={<SectionIcon name={section.icon} />}
              >
                 {section.items.map(({ field, display }) => (
                  <DetailItem
                    key={field.key}
                    label={field.label}
                    value={display}
                  />
                ))}
              </PropertySection>
            ))}





{(propertyDetails.contact?.appt_availability ||
  propertyDetails.contact?.appt_start_time ||
  propertyDetails.contact?.appt_end_time ||
  propertyDetails.contact?.appt_all_day) && (
  <PropertySection title="Schedule" icon={<Calendar size={20} />}>
    {propertyDetails.contact?.appt_availability && (
      <DetailItem
        label="Available Days"
        value={propertyDetails.contact.appt_availability}
      />
    )}
    {String(propertyDetails.contact?.appt_all_day).toUpperCase() === "Y" && (
      <DetailItem label="Available All Time" value="Yes" />
    )}
    {propertyDetails.contact?.appt_start_time &&
      String(propertyDetails.contact?.appt_all_day).toUpperCase() !== "Y" && (
        <DetailItem
          label="Start Time"
          value={formatTime(propertyDetails.contact.appt_start_time)}
        />
      )}
    {propertyDetails.contact?.appt_end_time &&
      String(propertyDetails.contact?.appt_all_day).toUpperCase() !== "Y" && (
        <DetailItem
          label="End Time"
          value={formatTime(propertyDetails.contact.appt_end_time)}
        />
      )}
  </PropertySection>
)}

{/* 📝 Description card — after Schedule, before Location */}
{propertyDescription && (
  <Tile
    className="property-card"
    style={{
      padding: "1rem 1.25rem 1.25rem",
      marginBottom: "1rem",
    }}
  >
    <div
      className="section-title"
      style={{
        display: "flex",
        alignItems: "center",
        gap: "0.5rem",
        marginBottom: "1rem",
        paddingBottom: "0.75rem",
        borderBottom: "1px solid #e0e0e0",
        fontSize: "1rem",
        fontWeight: 600,
        color: "#161616",
      }}
    >
      <Information size={20} />
      Description
    </div>

    <div
      style={{
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        lineHeight: 1.6,
        fontSize: "0.875rem",
        color: "#161616",
      }}
    >
      {propertyDescription}
    </div>
  </Tile>
)}
<Tile className="property-card location-card" style={{ padding: "0", overflow: "hidden" }}>
  <div style={{ padding: "1rem 1.25rem" }}>
    <div className="section-title" style={{ marginBottom: "0.5rem", borderBottom: "none", paddingBottom: 0 }}>
      <Location size={20} />
      Location
    </div>
    
    <p className="location-text-short" style={{ fontSize: "0.875rem", color: "#525252", margin: "0 0 1rem 0", lineHeight: "1.4" }}>
      {formatLocationShort(propertyDetails.location)}
    </p>

    <div style={{ display: "flex", gap: "0.75rem", marginBottom: "1rem" }}>
      <Button
        size="sm"
        kind="tertiary"
        renderIcon={Launch}
        onClick={() => openDirections(propertyDetails.location)}
      >
        View Location
      </Button>
      <Button
        size="sm"
        kind="primary"
        renderIcon={DirectionStraightRight}
        onClick={() => openDirections(propertyDetails.location)}
      >
        Get Direction
      </Button>
    </div>
  </div>

  {propertyDetails.location?.latitude && propertyDetails.location?.longitude ? (
    <div style={{ width: "100%", height: "300px", background: "#f4f4f4" }}>
      <iframe
        title="Property Location Map"
        width="100%"
        height="100%"
        frameBorder="0"
        style={{ border: 0 }}
        src={`https://maps.google.com/maps?q=${propertyDetails.location.latitude},${propertyDetails.location.longitude}&z=15&output=embed`}
        allowFullScreen
      ></iframe>
    </div>
  ) : (
    <div style={{ padding: "1rem", color: "#6f6f6f", fontSize: "0.875rem", background: "#f4f4f4", textAlign: "center" }}>
      Map coordinates not available.
    </div>
  )}
</Tile>
          </div>
        </Column>

        {/* SIDEBAR */}
        <Column lg={6} md={8} sm={4}>
          <Tile className="contact-card">
            <div className="contact-header">
              <User size={24} />
              <h3>Contact {isRental ? "Owner" : "Seller"}</h3>
            </div>
            <div className="contact-details">
              {propertyDetails.contact?.contact_person && (
                <p>Contact Person: {propertyDetails.contact.contact_person}</p>
              )}
              {propertyDetails.contact?.contact_person_number && (
                <p>Contact Person No: {propertyDetails.contact.contact_person_number}</p>
              )}
              {propertyDetails.contact?.contact_person_email && (
                <p>Contact Person Email: {propertyDetails.contact.contact_person_email}</p>
              )}
              {propertyDetails.contact?.owner_number && (
                <p>Owner Contact No: {propertyDetails.contact.owner_number}</p>
              )}
              {propertyDetails.contact?.owner_email && (
                <p>Owner Email: {propertyDetails.contact.owner_email}</p>
              )}
              {propertyDetails.contact?.posted_by && (
                <p>Posted By: {propertyDetails.contact.posted_by}</p>
              )}
            </div>
          </Tile>

          <Tile className="property-card">
            <div className="section-title">
              <Information size={18} />
              Property Status
            </div>
            <div className="status-grid">
              <StatusItem
                label="Verification"
                value={formatVerificationStatus(propertyDetails.verificationStatus)}
                isVerified={isVerified}
                isPending={isPending}
                isUnderVerification={isUnderVerification}
              />
              <StatusItem label="Status" value={propertyDetails.status} />
              <StatusItem label="Type" value={propertyDetails.postedFor} />
              <StatusItem label="Created By" value={propertyDetails.createdUser} />
            </div>
          </Tile>

          <Tile className="property-card">
            <div className="section-title">
              <Information size={18} />
              Quick Info
            </div>
            <div className="quick-info-grid">
              <QuickInfoItem label="Property ID" value={propertyDetails.propertySeqId} />
              <QuickInfoItem label="Type" value={propertyDetails.type} />
              <QuickInfoItem
                label="Created On"
                value={formatDate(propertyDetails.createdTimestamp)}
              />
            </div>
          </Tile>
        </Column>
      </Grid>
    </div>
  );
};

// =====================================================================
//  SUB-COMPONENTS
// =====================================================================
const SectionIcon = ({ name }) => {
  const map = {
    money: <Money size={20} />,
    building: <Building size={20} />,
    home: <Home size={20} />,
    ruler: <Ruler size={20} />,
    lightning: <Lightning size={20} />,
    store: <Store size={20} />,
    information: <Information size={20} />,
  };
  return map[name] || <Information size={20} />;
};

const KeyFeature = ({ icon, label, value }) => (
  <div className="key-feature">
    <div className="key-feature-icon">{icon}</div>
    <div className="key-feature-content">
      <span className="key-feature-label">{label}</span>
      <strong className="key-feature-value">{value || "-"}</strong>
    </div>
  </div>
);

const DetailItem = ({ label, value }) => (
  <div className="detail-item">
    <span className="detail-label">{label}</span>
    <strong className="detail-value">{value || "-"}</strong>
  </div>
);

const PropertySection = ({ title, icon, children }) => (
  <Tile className="property-card">
    <div className="section-title">
      {icon}
      {title}
    </div>
    <div className="details-grid">{children}</div>
  </Tile>
);

const StatusItem = ({ label, value, isVerified, isPending, isUnderVerification }) => (
  <div className="status-item">
    <span className="status-label">{label}</span>
    <div className="status-value-wrapper">
      {isVerified && <Checkmark size={16} className="status-icon verified" />}
      {(isPending || isUnderVerification) && (
        <Warning size={16} className="status-icon pending" />
      )}
      <strong className="status-value">{value || "-"}</strong>
    </div>
  </div>
);

const QuickInfoItem = ({ label, value }) => (
  <div className="quick-info-item">
    <span className="quick-info-label">{label}</span>
    <strong className="quick-info-value">{value || "-"}</strong>
  </div>
);

// =====================================================================
//  KeyFeatureGrid
// =====================================================================
const KeyFeatureGrid = ({
  renderedSections,
  formattedPrice,
  isFlatmates,
  isPG,
  isRental,
  isLand,
  isCommercial,
  isLease,
}) => {
  const findValue = (key) => {
    for (const s of renderedSections) {
      const hit = s.items.find((i) => i.field.key === key);
      if (hit) return hit.display;
    }
    return null;
  };

  const areaVal = () => {
    const bu = findValue("_built_up_area");
    const la = findValue("_land_area");
    return la || bu || "-";
  };

  if (isFlatmates) {
    const depositRaw = findValue("flat_expected_deposit");
    const depositDisplay = depositRaw
      ? `₹${Number(String(depositRaw).replace(/[^\d.]/g, "")).toLocaleString("en-IN")}`
      : "-";
    return (
      <div className="key-features-grid">
        <KeyFeature icon={<Money size={24} />} label="Monthly Rent" value={formattedPrice} />
        <KeyFeature icon={<Money size={24} />} label="Deposit" value={depositDisplay} />
        <KeyFeature icon={<Home size={24} />} label="BHK Type" value={findValue("flat_bhk_type") || "-"} />
        <KeyFeature icon={<Person size={24} />} label="Room Type" value={findValue("flat_room_type") || "-"} />
      </div>
    );
  }

if (isPG) {
  return (
    <div className="key-features-grid">
      <KeyFeature
        icon={<Money size={24} />}
        label="Expected Rent"
        value={findValue("room_expected_rent") || "-"}
      />

      <KeyFeature
        icon={<Money size={24} />}
        label="Expected Deposit"
        value={findValue("room_expected_deposit") || "-"}
      />

<KeyFeature
  icon={<Person size={24} />}
  label="PG available for"
  value={findValue("pg_place_available_for") || "-"}
/>

      <KeyFeature
        icon={<Calendar size={24} />}
        label="Available From"
        value={findValue("pg_available_from") || "-"}
      />
    </div>
  );
}

  if (isLand) {
    return (
      <div className="key-features-grid">
        <KeyFeature icon={<Ruler size={24} />} label="Land Area" value={areaVal()} />
        <KeyFeature icon={<Money size={24} />} label="Expected Price" value={formattedPrice} />
        <KeyFeature
          icon={<Building size={24} />}
          label="Floors Allowed"
          value={findValue("floors_allowed_for_construction") || "-"}
        />
        <KeyFeature
          icon={<Calendar size={24} />}
          label="Available From"
          value={findValue("land_resale_available_from") || "-"}
        />
      </div>
    );
  }

  if (isCommercial && isRental) {
    return (
      <div className="key-features-grid">
        <KeyFeature icon={<Store size={24} />} label="Property Type" value={findValue("com_rent_property_type") || "-"} />
        <KeyFeature icon={<Building size={24} />} label="Building Type" value={findValue("com_rent_building_type") || "-"} />
        <KeyFeature icon={<Ruler size={24} />} label="Built-up Area" value={areaVal()} />
        <KeyFeature icon={<Money size={24} />} label="Monthly Rent" value={formattedPrice} />
      </div>
    );
  }

  if (isCommercial && !isRental) {
    return (
      <div className="key-features-grid">
        <KeyFeature icon={<Store size={24} />} label="Property Type" value={findValue("com_sale_property_type") || "-"} />
        <KeyFeature icon={<Ruler size={24} />} label="Built-up Area" value={areaVal()} />
        <KeyFeature icon={<Money size={24} />} label="Expected Price" value={formattedPrice} />
        <KeyFeature icon={<Building size={24} />} label="Floor" value={findValue("floor") || "-"} />
      </div>
    );
  }

  // Residential Rent — split Lease vs Rent
  if (isRental) {
    const rentLabel = isLease ? "Expected Lease Amount" : "Monthly Rent";

    const secondaryCard = isLease
      ? {
          icon: <Calendar size={24} />,
          label: "Available From",
          value: findValue("rent_available_from") || "-",
        }
      : {
          icon: <Money size={24} />,
          label: "Deposit",
          value: findValue("rent_expected_deposit") || "-",
        };

    return (
      <div className="key-features-grid">
        <KeyFeature
          icon={<Money size={24} />}
          label={rentLabel}
          value={formattedPrice}
        />
        <KeyFeature {...secondaryCard} />
        <KeyFeature
          icon={<Home size={24} />}
          label="BHK Type"
          value={findValue("bhk_type") || "-"}
        />
        <KeyFeature
          icon={<Building size={24} />}
          label="Property Type"
          value={findValue("apartment_type") || "-"}
        />
      </div>
    );
  }

  // Residential sale
  return (
    <div className="key-features-grid">
      <KeyFeature icon={<Home size={24} />} label="BHK Type" value={findValue("resale_bhk_type") || "-"} />
      <KeyFeature icon={<Money size={24} />} label="Price" value={formattedPrice} />
      <KeyFeature
        icon={<Building size={24} />}
        label="Furnishing"
        value={findValue("ren_resale_furnishing") || "-"}
      />
    </div>
  );
};

// =====================================================================
//  HELPERS
// =====================================================================
const formatPrice = (start, end) => {
  if (!start && !end) return "N/P";
  if (start && end && Number(start) !== Number(end)) {
    return `₹${Number(start).toLocaleString("en-IN")} - ₹${Number(end).toLocaleString("en-IN")}`;
  }
  return `₹${Number(start || end).toLocaleString("en-IN")}`;
};

const formatLocation = (loc) => {
  if (!loc || typeof loc !== "object") return "-";
  const parts = [
    loc.address_line_1,
    loc.address_line_2,
    loc.area_name,
    loc.village_name,
    loc.taluk_name,
    loc.district_name,
    loc.state_name,
    loc.pin_code,
  ].filter(Boolean);
  if (loc.landmark) parts.unshift(loc.landmark);
  return parts.length ? parts.join(", ") : "-";
};

const formatLocationShort = (loc) => {
  if (!loc || typeof loc !== "object") return "-";
  const parts = [
    loc.address_line_1,
    loc.address_line_2,
    loc.landmark ? `near ${loc.landmark}` : null,
    loc.area_name,
    loc.village_name,
    loc.taluk_name,
    loc.district_name,
    loc.state_name,
    loc.pin_code,
  ].filter(Boolean);
  return parts.length ? parts.join(", ") : "-";
};

const formatDate = (dateString) => {
  if (!dateString) return "-";
  try {
    const d = new Date(dateString);
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return dateString;
  }
};

const formatDateOnly = (value) => {
  if (!value) return null;
  const s = String(value);
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s;
  const d = new Date(s);
  if (!isNaN(d.getTime())) {
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  }
  return s;
};

const formatTime = (timeString) => {
  if (!timeString || timeString === "-") return "-";
  try {
    const d = new Date(timeString);
    if (!isNaN(d.getTime())) {
      return d.toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    }
    return timeString;
  } catch {
    return timeString;
  }
};

const getMediaUrl = (item) =>
  item?.media_url ||
  item?.MEDIA_URL ||
  item?.FILE_URL ||
  item?.file_url ||
  item?.url ||
  item?.URL ||
  item?.DOCUMENT_URL ||
  "";

const openDirections = (location) => {
  if (!location) return;
  if (location.latitude && location.longitude) {
    window.open(
      `https://www.google.com/maps/search/?api=1&query=${location.latitude},${location.longitude}`,
      "_blank"
    );
    return;
  }
  const address = formatLocation(location);
  if (address && address !== "-") {
    window.open(
      `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`,
      "_blank"
    );
  }
};

export default PropertyView;